import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PageHead } from 'src/ui/patterns/PageHead';
import { Input } from 'src/ui/Input';
import { Button } from 'src/ui/Button';
import { Checkbox } from 'src/ui/Checkbox';
import { Badge } from 'src/ui/Badge';
import { LoadingBlock } from 'src/ui/Spinner';
import { EmptyState } from 'src/ui/EmptyState';
import { useToast } from 'src/ui/Toast';
import {
  useAdminWinBackPreviewMutation,
  useAdminWinBackApplyMutation,
  useLazyAdminWinBackRunQuery,
  useAdminWinBackRunsQuery,
  WinBackEligibleUser,
  WinBackCityFacet,
  WinBackRunAttempt,
  WinBackAttemptStatus,
} from 'src/store/suggestionsApi';
import UserFilters, {
  userFiltersToQueryParams,
  UserFilterState,
} from 'src/components/UserFilters';
import { AccountStatus, UserType, ProfileGender } from 'src/store/userApi';

const GENDER_SHORT: Record<number, string> = {
  [ProfileGender.FEMALE]: 'F',
  [ProfileGender.MALE]: 'M',
  [ProfileGender.NON_BINARY]: 'NB',
};

const locationText = (u: WinBackEligibleUser): string => {
  const parts = [u.city, u.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

/** ACTIVE + NORMAL are win-back's safety baseline: always on, never removable. */
const LOCKED_FILTERS: UserFilterState = {
  accountStatus: [AccountStatus.ACTIVE],
  userType: [UserType.NORMAL],
};

const daysAgo = (iso: string | null): string => {
  if (!iso) return 'never';
  const d = Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'today' : `${d}d ago`;
};

const dateTime = (iso: string): string => new Date(iso).toLocaleString();

const STATUS_COLOR: Record<WinBackAttemptStatus, string> = {
  sent: 'green',
  no_result: 'gray',
  error: 'red',
  pending: 'amber',
};
const STATUS_LABEL: Record<WinBackAttemptStatus, string> = {
  sent: 'Sent',
  no_result: 'No result',
  error: 'Error',
  pending: 'Pending',
};

// ---- Section shell ---------------------------------------------------------

function Card({
  title,
  right,
  children,
}: {
  title: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-canvas)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div
        className="row"
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          padding: '13px 16px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span className="panel-title">{title}</span>
        {right}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

// ---- Trigger: inputs → selectable preview → send ---------------------------

function TriggerCard({ onStarted }: { onStarted: (runId: string) => void }) {
  const push = useToast();
  const [days, setDays] = useState('14');
  const [notSuggested, setNotSuggested] = useState('30');
  const [filters, setFilters] = useState<UserFilterState>(LOCKED_FILTERS);
  const [cities, setCities] = useState<Set<string>>(new Set());
  const [citySearch, setCitySearch] = useState('');
  const [eligible, setEligible] = useState<WinBackEligibleUser[] | null>(null);
  const [cityFacets, setCityFacets] = useState<WinBackCityFacet[]>([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [preview, { isLoading: previewing }] = useAdminWinBackPreviewMutation();
  const [apply, { isLoading: applying }] = useAdminWinBackApplyMutation();

  const parsedDays = parseInt(days, 10);
  const validDays = Number.isFinite(parsedDays) && parsedDays >= 1;
  const parsedNot = parseInt(notSuggested, 10);

  // Any change to the dormancy window or advanced filters invalidates the
  // current preview — force a fresh Preview so counts/facets stay honest.
  const invalidate = () => {
    setEligible(null);
    setCityFacets([]);
    setCities(new Set());
  };

  const runPreview = async (citySel: Set<string>) => {
    try {
      const res = await preview({
        inactiveDays: parsedDays,
        notSuggestedDays: Number.isFinite(parsedNot) ? parsedNot : 0,
        ...userFiltersToQueryParams(filters),
        cities: citySel.size ? [...citySel] : undefined,
      }).unwrap();
      setEligible(res.data.users);
      setCityFacets(res.data.cityFacets ?? []);
      setTotal(res.data.total);
      setCapped(res.data.capped);
      setSelected(new Set(res.data.users.map((u) => u.userId)));
    } catch (e: any) {
      push({
        title: 'Error',
        message: e?.data?.message || 'Failed to preview',
        type: 'error',
      });
    }
  };

  const doSend = async () => {
    const userIds = [...selected];
    if (!userIds.length) return;
    try {
      const res = await apply({ userIds }).unwrap();
      push({
        title: 'Win-back started',
        message: res.message || `Dispatched ${userIds.length} jobs`,
        type: 'success',
      });
      setEligible(null);
      setCityFacets([]);
      setCities(new Set());
      setSelected(new Set());
      if (res.data?.runId) onStarted(res.data.runId);
    } catch (e: any) {
      push({
        title: 'Error',
        message: e?.data?.message || 'Failed to start win-back',
        type: 'error',
      });
    }
  };

  const toggle = (userId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const toggleCity = (city: string) => {
    const next = new Set(cities);
    if (next.has(city)) next.delete(city);
    else next.add(city);
    setCities(next);
    runPreview(next);
  };

  const visibleFacets = cityFacets.filter((f) =>
    f.city.toLowerCase().includes(citySearch.trim().toLowerCase())
  );

  const th = (label: string, w?: string): React.CSSProperties => ({
    textAlign: 'left',
    padding: '7px 10px',
    fontSize: 'var(--fs-2xs)',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-subtle)',
    position: 'sticky',
    top: 0,
    width: w,
  });
  const td: React.CSSProperties = { padding: '7px 10px' };

  return (
    <Card title="New win-back run">
      <div className="col" style={{ gap: 12 }}>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          Send a fresh suggestion (chat card + push) to real, active users who
          haven&apos;t opened the app in a while and weren&apos;t recently
          suggested to. Narrow with filters — including a city, to nudge people
          in the same area together — then preview, trim, and send.
        </span>

        <div
          className="row"
          style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <span style={{ fontSize: 'var(--fs-sm)' }}>Inactive for</span>
          <div style={{ width: 80 }}>
            <Input
              type="number"
              min={1}
              value={days}
              onChange={(e) => {
                setDays(e.target.value);
                invalidate();
              }}
            />
          </div>
          <span style={{ fontSize: 'var(--fs-sm)' }}>
            days, not suggested in
          </span>
          <div style={{ width: 80 }}>
            <Input
              type="number"
              min={0}
              value={notSuggested}
              onChange={(e) => {
                setNotSuggested(e.target.value);
                invalidate();
              }}
            />
          </div>
          <span style={{ fontSize: 'var(--fs-sm)' }}>days</span>
          <UserFilters
            value={filters}
            locked={LOCKED_FILTERS}
            onChange={(next) => {
              setFilters(next);
              invalidate();
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={!validDays || previewing}
            loading={previewing}
            onClick={() => runPreview(cities)}
          >
            Preview
          </Button>
        </div>

        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
          Active, normal accounts are always included (win-back safety) — other
          filters narrow within that set.
        </span>

        {eligible && cityFacets.length > 0 && (
          <div
            className="col"
            style={{
              gap: 8,
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
            }}
          >
            <div
              className="row"
              style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                Location
              </span>
              {cities.size > 0 && (
                <span
                  style={{
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {total} in {cities.size}{' '}
                  {cities.size === 1 ? 'area' : 'areas'}
                </span>
              )}
            </div>
            <Input
              size="sm"
              placeholder="Search a city"
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
            />
            <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
              {visibleFacets.map((f) => {
                const on = cities.has(f.city);
                return (
                  <button
                    key={f.city}
                    type="button"
                    className={
                      on ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'
                    }
                    onClick={() => toggleCity(f.city)}
                  >
                    {f.city} <span style={{ opacity: 0.7 }}>{f.count}</span>
                  </button>
                );
              })}
              {visibleFacets.length === 0 && (
                <span
                  style={{
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  No cities match.
                </span>
              )}
            </div>
          </div>
        )}

        {eligible && (
          <div className="col" style={{ gap: 8 }}>
            <div
              className="row"
              style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                {selected.size} of {eligible.length} selected
                {capped
                  ? ` (showing first ${eligible.length} of ${total})`
                  : ''}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setSelected(new Set(eligible.map((u) => u.userId)))
                }
              >
                Select all
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelected(new Set())}
              >
                Select none
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={selected.size === 0 || applying}
                loading={applying}
                onClick={doSend}
              >
                Send win-back to {selected.size}
              </Button>
            </div>
            {eligible.length === 0 ? (
              <EmptyState
                icon="check-circle"
                title="No users match"
                desc="Nobody is eligible for these settings."
              />
            ) : (
              <div
                style={{
                  maxHeight: 360,
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 'var(--fs-sm)',
                  }}
                >
                  <thead>
                    <tr>
                      <th style={th('', '34px')} />
                      <th style={th('Name')}>Name</th>
                      <th style={th('Email')}>Email</th>
                      <th style={th('Gender', '58px')}>Gender</th>
                      <th style={th('Age', '48px')}>Age</th>
                      <th style={th('Location')}>Location</th>
                      <th style={th('Inactive', '96px')}>Inactive</th>
                      <th style={th('Last sugg.', '110px')}>Last sugg.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligible.map((u) => (
                      <tr
                        key={u.userId}
                        style={{
                          borderBottom: '1px solid var(--border)',
                          cursor: 'pointer',
                        }}
                        onClick={() => toggle(u.userId)}
                      >
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected.has(u.userId)}
                            onChange={() => toggle(u.userId)}
                          />
                        </td>
                        <td style={{ ...td, fontWeight: 500 }}>
                          {u.name || u.userId.slice(0, 8)}
                        </td>
                        <td
                          style={{
                            ...td,
                            color: 'var(--text-secondary)',
                            wordBreak: 'break-all',
                          }}
                        >
                          {u.email || '—'}
                        </td>
                        <td style={{ ...td, color: 'var(--text-secondary)' }}>
                          {u.gender != null
                            ? GENDER_SHORT[u.gender] ?? '—'
                            : '—'}
                        </td>
                        <td style={{ ...td, color: 'var(--text-secondary)' }}>
                          {u.age ?? '—'}
                        </td>
                        <td style={{ ...td, color: 'var(--text-secondary)' }}>
                          {locationText(u)}
                        </td>
                        <td style={{ ...td, color: 'var(--text-muted)' }}>
                          {daysAgo(u.lastRequestAt)}
                        </td>
                        <td style={{ ...td, color: 'var(--text-faint)' }}>
                          {daysAgo(u.lastSuggestionAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---- Run detail (per-user attempts) ----------------------------------------

function RunDetail({ runId }: { runId: string }) {
  const [fetchRun] = useLazyAdminWinBackRunQuery();
  const [attempts, setAttempts] = useState<WinBackRunAttempt[] | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetchRun({ runId }).unwrap();
      setAttempts(res.data.attempts);
    } catch {
      /* transient — the next poll retries */
    }
  }, [runId, fetchRun]);

  // Load on run change, then poll while any attempt is still pending.
  useEffect(() => {
    setAttempts(null);
    refresh();
  }, [runId, refresh]);

  useEffect(() => {
    if (!attempts) return;
    if (!attempts.some((a) => a.status === 'pending')) return;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [attempts, refresh]);

  if (!attempts) return <LoadingBlock label="Loading run…" />;

  const counts = attempts.reduce(
    (acc, a) => ({ ...acc, [a.status]: (acc[a.status] ?? 0) + 1 }),
    {} as Record<string, number>
  );
  const order: WinBackAttemptStatus[] = [
    'pending',
    'sent',
    'no_result',
    'error',
  ];
  const sorted = [...attempts].sort(
    (a, b) => order.indexOf(a.status) - order.indexOf(b.status)
  );

  return (
    <div className="col" style={{ gap: 10 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {order.map((s) => (
          <Badge key={s} color={STATUS_COLOR[s]} size="sm">
            {STATUS_LABEL[s]} {counts[s] ?? 0}
          </Badge>
        ))}
      </div>
      <div
        style={{
          maxHeight: 400,
          overflowY: 'auto',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--fs-sm)',
          }}
        >
          <thead>
            <tr>
              {[
                ['User', 'auto'],
                ['Status', '110px'],
                ['Suggested', 'auto'],
                ['Score', '64px'],
                ['Targeted', '150px'],
                ['Updated', '150px'],
              ].map(([label, w]) => (
                <th
                  key={label}
                  style={{
                    textAlign: 'left',
                    padding: '7px 12px',
                    fontSize: 'var(--fs-2xs)',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--border)',
                    background: 'var(--bg-subtle)',
                    position: 'sticky',
                    top: 0,
                    width: w,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((a) => (
              <tr
                key={a.userId}
                style={{ borderBottom: '1px solid var(--border)' }}
              >
                <td style={{ padding: '7px 12px' }}>
                  <div style={{ fontWeight: 500 }}>
                    {a.name || a.userId.slice(0, 8)}
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 'var(--fs-2xs)',
                      color: 'var(--text-faint)',
                    }}
                  >
                    {a.userId.slice(0, 8)}
                  </div>
                </td>
                <td style={{ padding: '7px 12px' }}>
                  <Badge color={STATUS_COLOR[a.status]} size="sm">
                    {STATUS_LABEL[a.status]}
                  </Badge>
                </td>
                <td
                  style={{
                    padding: '7px 12px',
                    color: 'var(--text-secondary)',
                  }}
                  title={a.suggestionId ?? undefined}
                >
                  {a.candidateName || (a.suggestionId ? '—' : '')}
                </td>
                <td
                  className="mono"
                  style={{
                    padding: '7px 12px',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {a.score != null ? a.score.toFixed(2) : ''}
                </td>
                <td
                  style={{ padding: '7px 12px', color: 'var(--text-muted)' }}
                  title={dateTime(a.targetedAt)}
                >
                  {daysAgo(a.targetedAt)}
                </td>
                <td
                  style={{ padding: '7px 12px', color: 'var(--text-muted)' }}
                  title={dateTime(a.updatedAt)}
                >
                  {a.status === 'pending' ? '—' : dateTime(a.updatedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Page ------------------------------------------------------------------

const WinBack = () => {
  const { data, isLoading, refetch } = useAdminWinBackRunsQuery(undefined, {
    // Refetch on focus so a run kicked off elsewhere shows up.
    refetchOnMountOrArgChange: true,
  });
  const runs = useMemo(() => data?.data ?? [], [data]);
  const [selectedRun, setSelectedRun] = useState<string | null>(null);

  // Rehydrate: default to the latest run once runs load (survives refresh).
  useEffect(() => {
    if (!selectedRun && runs.length > 0) setSelectedRun(runs[0].runId);
  }, [runs, selectedRun]);

  const onStarted = (runId: string) => {
    setSelectedRun(runId);
    refetch();
  };

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      <PageHead
        title="Win-back"
        subtitle="Re-engage dormant users with a fresh suggestion"
      />
      <div className="grow scroll-y" style={{ padding: 22 }}>
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <TriggerCard onStarted={onStarted} />

          <Card
            title="Runs"
            right={
              <Button
                variant="ghost"
                size="sm"
                leftIcon="refresh"
                onClick={() => refetch()}
              >
                Refresh
              </Button>
            }
          >
            {isLoading ? (
              <LoadingBlock label="Loading runs…" />
            ) : runs.length === 0 ? (
              <EmptyState
                icon="clock"
                title="No runs yet"
                desc="Preview and send a win-back above to create your first run."
              />
            ) : (
              <div className="col" style={{ gap: 6 }}>
                {runs.map((r) => {
                  const active = r.runId === selectedRun;
                  return (
                    <button
                      key={r.runId}
                      onClick={() => setSelectedRun(r.runId)}
                      style={{
                        textAlign: 'left',
                        background: active ? 'var(--bg-subtle)' : 'transparent',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        padding: '9px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        className="row"
                        style={{
                          gap: 10,
                          alignItems: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}
                        >
                          {dateTime(r.startedAt)}
                        </span>
                        <span
                          style={{
                            fontSize: 'var(--fs-xs)',
                            color: 'var(--text-muted)',
                          }}
                        >
                          {r.total} targeted
                        </span>
                        <span className="row" style={{ gap: 6 }}>
                          <Badge color="green" size="sm">
                            {r.sent} sent
                          </Badge>
                          <Badge color="gray" size="sm">
                            {r.noResult} no-result
                          </Badge>
                          {r.pending > 0 && (
                            <Badge color="amber" size="sm">
                              {r.pending} pending
                            </Badge>
                          )}
                          {r.error > 0 && (
                            <Badge color="red" size="sm">
                              {r.error} error
                            </Badge>
                          )}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>

          {selectedRun && (
            <Card title="Run detail">
              <RunDetail runId={selectedRun} />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default WinBack;
